import styled, { css } from 'styled-components';
import { Row, Col, TableRow } from '@pickit/pickit-grid-system';
import { TableFontSizesType, TableSizesType } from '../ThemeProvider/types';

export const TableHeaders = styled(TableRow)<{
  size: TableSizesType;
  fontSize: TableFontSizesType;
  collapse?: boolean;
  dynamicHeight?: boolean;
  hasScroll?: boolean;
}>`
  align-content: center;
  background-color: ${({ theme }) => theme.grayF0};
  border: 1px solid ${({ theme }) => theme.grayCC};
  border-radius: 10px 10px 0 0;
  color: ${({ theme }) => theme.gray2D};
  font-size: ${({ theme, fontSize }) => theme.table.fontSizes[fontSize]}px;
  ${({ theme, size, dynamicHeight, fontSize }) =>
    dynamicHeight
      ? css`
          max-height: ${theme.table.sizes[size].maxHeight[fontSize]};
          min-height: ${theme.table.sizes.small.rowHeight[fontSize]}px;
        `
      : css`
          height: ${theme.table.sizes[size].rowHeight[fontSize]}px;
        `}

  margin: 0 !important;
  ${({ collapse, hasScroll }) => {
    let padding = collapse ? 56 : 16;
    padding = hasScroll ? padding + 8 : padding;
    return css`
      padding-right: ${padding}px;
    `;
  }}
`;

export const TableCollapsibleRow = styled(Row)<{ isLastRow: boolean }>`
  background-color: ${({ theme }) => theme.white};
  border-bottom: 1px solid ${({ theme }) => theme.grayCC};
  margin: 0 !important;
  padding-right: 16px;
  ${({ isLastRow }) =>
    isLastRow &&
    css`
      border-bottom: none;
    `}
`;
export const TableCollapsibleContainer = styled(Col)`
  padding-left: 0 !important;
`;

export const TableData = styled(TableRow)<{
  hover: boolean;
  collapsible?: boolean;
  isLastRow?: boolean;
  pointer?: boolean;
  isGroupHeader?: boolean;
  size: TableSizesType;
  fontSize: TableFontSizesType;
  dynamicHeight?: boolean;
}>`
  align-items: center;
  background-color: ${({ theme }) => theme.white};
  border-bottom: 1px solid ${({ theme }) => theme.grayCC};
  ${({ collapsible }) =>
    collapsible &&
    css`
      border: none;
    `}
  color: ${({ theme }) => theme.gray4D};
  font-size: ${({ theme, fontSize }) => theme.table.fontSizes[fontSize]}px;
  ${({ isGroupHeader }) =>
    isGroupHeader &&
    css`
      font-weight: 600;
    `}

  ${({ theme, size, dynamicHeight, fontSize }) =>
    dynamicHeight
      ? css`
          max-height: ${theme.table.sizes[size].maxHeight[fontSize]};
          min-height: ${theme.table.sizes.small.rowHeight[fontSize]}px;
        `
      : css`
          height: ${theme.table.sizes[size].rowHeight[fontSize]}px;
        `}

  margin: 0 !important;
  padding-right: 16px;
  ${({ pointer }) =>
    pointer &&
    css`
      cursor: pointer !important;
    `}
  ${({ hover }) =>
    hover &&
    css`
      &:hover {
        background-color: ${({ theme }) => theme.grayF5};
      }
    `}
    ${({ isLastRow }) =>
    isLastRow &&
    css`
      border-bottom: none;
    `}
`;

export const CollapsedRow = styled(TableData)<{ groupCollapse: boolean }>`
  ${({ groupCollapse }) =>
    groupCollapse
      ? css`
          font-size: 14px;
        `
      : css`
          font-size: 12px;
        `}
  height: unset;
  padding-bottom: 8px;
  padding-right: 40px;
  & > * {
    justify-content: flex-start !important;
  }
  & > ${Col} > p {
    margin-bottom: 16px;
  }
`;

export const NoResultRow = styled(TableData)`
  align-items: center;
  background-color: ${({ theme }) => theme.grayF5};
  border: 1px solid ${({ theme }) => theme.grayCC};
  border-radius: 0 0 10px 10px;
  border-top: none;
  color: ${({ theme }) => theme.gray80};
  display: flex;
  font-weight: 700;
  justify-content: center;
`;

export const TableContainer = styled.table`
  width: 100%;
`;

export const RowsContainer = styled.div<{
  rowCount: number;
  size: TableSizesType;
  fontSize: TableFontSizesType;
}>`
  border: 1px solid ${({ theme }) => theme.grayCC};
  border-radius: 0 0 10px 10px;
  border-top: none;
  &::-webkit-scrollbar {
    height: 6px;
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #b8b8b8;
    background-clip: padding-box;
    border-radius: 4px;
    height: 6px;
    width: 8px;
  }
  max-height: ${({ rowCount, theme, size, fontSize }) =>
    rowCount * theme.table.sizes[size].rowHeight[fontSize]}px;
  overflow-y: auto;
  @-moz-document url-prefix() {
    scrollbar-width: thin;
  }
  transition: all ${({ theme: { collapsible } }) => collapsible.duration}ms ease;
  @supports (overflow: overlay) {
    overflow-y: overlay;
  }
`;

export const TextContainer = styled.p<{ lineHeight: number; color?: string; weight?: number }>`
  color: ${({ color }) => color};
  display: inline-block;
  font-weight: ${({ weight }) => weight};

  line-height: ${({ lineHeight }) => lineHeight}px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

export const TableTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

export const TableTitleWrapper = styled.span<{
  align: string;
}>`
  align-items: center;
  display: flex;
  justify-content: ${({ align }) => align};
`;

export const TooltipWrapper = styled.span`
  margin-bottom: 8px;
  margin-left: 2px;
`;

export const TablePaginatorContainer = styled.div`
  padding-top: 24px;
`;
